import { useEffect, useState } from 'react';
import { HostSection } from '../components/host/HostLayout';
import RevenueReports from '../components/reports/RevenueReports';
import OccupancyReports from '../components/reports/OccupancyReports';
import PropertyPerformance from '../components/reports/PropertyPerformance';
import TourPerformance from '../components/reports/TourPerformance';
import {
  hostReportsMeta,
  hostRevenue,
  hostOccupancy,
  hostPropertyPerformance,
  hostTourPerformance,
  hostExportReport,
} from '../api/reports';
import { useToast } from '../components/ToastContext';

const tabs = [
  { key: 'revenue', label: 'Revenue reports' },
  { key: 'occupancy', label: 'Occupancy reports' },
  { key: 'property', label: 'Property performance' },
  { key: 'tour', label: 'Tour performance' },
];

const toLocalIsoDate = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const todayIso = () => toLocalIsoDate(new Date());
const daysAgoIso = (days) => {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return toLocalIsoDate(d);
};

const buildDefaultFilters = () => {
  const range = { from: daysAgoIso(30), to: todayIso() };
  return {
    revenue: { ...range, groupBy: 'day', type: 'all', propertyId: '', tourId: '' },
    occupancy: { ...range, groupBy: 'day', propertyId: '' },
    property: { ...range, propertyId: '' },
    tour: { ...range, tourId: '' },
  };
};

const HostReports = ({ accessToken }) => {
  const toast = useToast();
  const [activeTab, setActiveTab] = useState('revenue');
  const [filters, setFilters] = useState(buildDefaultFilters);
  const [meta, setMeta] = useState({ properties: [], tours: [] });
  const [loadingMeta, setLoadingMeta] = useState(false);
  const [loading, setLoading] = useState({ revenue: false, occupancy: false, property: false, tour: false });
  const [exporting, setExporting] = useState(false);
  const [data, setData] = useState({ revenue: null, occupancy: null, property: [], tour: [] });
  const [error, setError] = useState('');

  const loadMeta = async () => {
    if (!accessToken) return;
    setLoadingMeta(true);
    try {
      const res = await hostReportsMeta(accessToken);
      setMeta(res || { properties: [], tours: [] });
    } catch (err) {
      toast.error(err.message || 'Failed to load dropdowns');
    } finally {
      setLoadingMeta(false);
    }
  };

  const loadTab = async (tab, overrideFilters) => {
    if (!accessToken) return;
    const tabFilters = overrideFilters || filters[tab];
    setError('');
    setLoading((s) => ({ ...s, [tab]: true }));
    try {
      if (tab === 'revenue') {
        const res = await hostRevenue(accessToken, tabFilters);
        setData((d) => ({ ...d, revenue: res }));
      } else if (tab === 'occupancy') {
        const res = await hostOccupancy(accessToken, tabFilters);
        setData((d) => ({ ...d, occupancy: res }));
      } else if (tab === 'property') {
        const res = await hostPropertyPerformance(accessToken, tabFilters);
        setData((d) => ({ ...d, property: res }));
      } else if (tab === 'tour') {
        const res = await hostTourPerformance(accessToken, tabFilters);
        setData((d) => ({ ...d, tour: res }));
      }
    } catch (err) {
      setError(err.message || 'Failed to load report');
    } finally {
      setLoading((s) => ({ ...s, [tab]: false }));
    }
  };

  useEffect(() => {
    loadMeta();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  useEffect(() => {
    loadTab(activeTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, accessToken]);

  const handleApply = (tab, nextFilters) => {
    setFilters((f) => ({ ...f, [tab]: nextFilters }));
    loadTab(tab, nextFilters);
  };

  const handleReset = (tab) => {
    const defaults = buildDefaultFilters();
    setFilters(defaults);
    loadTab(tab, defaults[tab]);
  };

  const handleExport = async (tab, tabFilters) => {
    setExporting(true);
    try {
      const typeMap = { revenue: 'revenue', occupancy: 'occupancy', property: 'property', tour: 'tour' };
      const csv = await hostExportReport(accessToken, { ...tabFilters, type: typeMap[tab] });
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${typeMap[tab] || 'report'}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Export ready');
    } catch (err) {
      toast.error(err.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const renderTab = () => {
    if (activeTab === 'revenue') {
      return (
        <RevenueReports
          data={data.revenue}
          filters={filters.revenue}
          onFiltersChange={(next) => setFilters((f) => ({ ...f, revenue: next }))}
          onApply={(next) => handleApply('revenue', next)}
          onReset={() => handleReset('revenue')}
          onExport={(next) => handleExport('revenue', next)}
          properties={meta.properties}
          tours={meta.tours}
          loading={loading.revenue}
          exporting={exporting}
          loadingMeta={loadingMeta}
        />
      );
    }
    if (activeTab === 'occupancy') {
      return (
        <OccupancyReports
          data={data.occupancy}
          filters={filters.occupancy}
          onFiltersChange={(next) => setFilters((f) => ({ ...f, occupancy: next }))}
          onApply={(next) => handleApply('occupancy', next)}
          onReset={() => handleReset('occupancy')}
          onExport={(next) => handleExport('occupancy', next)}
          properties={meta.properties}
          loading={loading.occupancy}
          exporting={exporting}
          loadingMeta={loadingMeta}
        />
      );
    }
    if (activeTab === 'property') {
      return (
        <PropertyPerformance
          data={data.property}
          filters={filters.property}
          onFiltersChange={(next) => setFilters((f) => ({ ...f, property: next }))}
          onApply={(next) => handleApply('property', next)}
          onReset={() => handleReset('property')}
          onExport={(next) => handleExport('property', next)}
          properties={meta.properties}
          loading={loading.property}
          exporting={exporting}
          loadingMeta={loadingMeta}
        />
      );
    }
    if (activeTab === 'tour') {
      return (
        <TourPerformance
          data={data.tour}
          filters={filters.tour}
          onFiltersChange={(next) => setFilters((f) => ({ ...f, tour: next }))}
          onApply={(next) => handleApply('tour', next)}
          onReset={() => handleReset('tour')}
          onExport={(next) => handleExport('tour', next)}
          tours={meta.tours}
          loading={loading.tour}
          exporting={exporting}
          loadingMeta={loadingMeta}
        />
      );
    }
    return null;
  };

  return (
    <HostSection title="Reports" description="Analyze occupancy, revenue, and cancellations.">
      <div className="grid gap-4 lg:grid-cols-[240px,1fr]">
        <aside className="rounded-2xl border border-slate-200 bg-slate-900 text-white shadow-sm">
          <div className="border-b border-slate-800 px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-300">
            Reports
          </div>
          <div className="p-3 space-y-2">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key)}
                className={`flex w-full items-center justify-between rounded-lg px-3 py-2 text-sm font-semibold transition ${
                  activeTab === tab.key ? 'bg-white text-slate-900 shadow-sm' : 'bg-slate-800 text-slate-200 hover:bg-slate-700'
                }`}
              >
                <span>{tab.label}</span>
                <span className="h-2 w-2 rounded-full bg-current" />
              </button>
            ))}
          </div>
        </aside>

        <div className="space-y-4">
          {loadingMeta && (
            <div className="rounded-xl border border-slate-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              Loading properties and tours...
            </div>
          )}
          {error && (
            <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
              {error}
            </div>
          )}
          {renderTab()}
        </div>
      </div>
    </HostSection>
  );
};

export default HostReports;
